import React, { useState, useRef, useEffect } from 'react'
import {
  UseFormRegister,
  UseFormHandleSubmit,
  useFieldArray,
  Control,
  useWatch,
  UseFormSetValue,
} from 'react-hook-form'
import {
  SlideExecutionPlan,
  ExecutionPlanFormData,
} from '../hooks/execution_plan/types'
import { SlideContainer } from '../components/slide_container'
import { SlideHeader } from '../components/slide_header'
import {
  AiRegenerateSection,
  AiInstructionFormData,
} from '../components/ai_regenerate_section'
import FlatButton from '../../../components/buttons/flat_button'
import { TextInput } from '../../../components/form/inputs/TextInput'
import { TextareaInput } from '../../../components/form/inputs/TextareaInput'
import logoGray from '../../../images/logo_gray.png'

interface ExecutionPlanSlideProps {
  slideExecutionPlan: SlideExecutionPlan | null
  isEditing: boolean
  isUpdating: boolean
  isDirty: boolean
  register: UseFormRegister<ExecutionPlanFormData>
  handleSubmit: UseFormHandleSubmit<ExecutionPlanFormData>
  control: Control<ExecutionPlanFormData>
  setValue: UseFormSetValue<ExecutionPlanFormData>
  registerAi: UseFormRegister<AiInstructionFormData>
  handleSubmitAi: UseFormHandleSubmit<AiInstructionFormData>
  aiInstruction: string
  onFormSubmit: (data: ExecutionPlanFormData) => Promise<void>
  onAiRegenerate: (data: AiInstructionFormData) => Promise<void>
}

interface DraggingInfo {
  index: number
  type: 'start' | 'end' | 'move'
  initialX: number
  initialPeriod: number
  initialStartPeriod?: number
  initialEndPeriod?: number
}

export const ExecutionPlanSlide = React.forwardRef<
  HTMLDivElement,
  ExecutionPlanSlideProps
>(
  (
    {
      slideExecutionPlan,
      isEditing,
      isUpdating,
      isDirty,
      register,
      handleSubmit,
      control,
      setValue,
      registerAi,
      handleSubmitAi,
      aiInstruction,
      onFormSubmit,
      onAiRegenerate,
    },
    ref
  ) => {
    const {
      fields: scheduleFields,
      append: appendSchedule,
      remove: removeSchedule,
      update: updateSchedule,
    } = useFieldArray({
      control,
      name: 'scheduleItems',
    })

    const watchedScheduleItems = useWatch({
      control,
      name: 'scheduleItems',
    })

    const watchedMaxPeriod = useWatch({
      control,
      name: 'maxPeriod',
    })

    const currentMaxPeriod = watchedMaxPeriod || 24
    const [dragging, setDragging] = useState<DraggingInfo | null>(null)
    const scheduleContainerRef = useRef<HTMLDivElement>(null)

    const handleAddSchedule = () => {
      // 項目を追加時のデフォルト設定
      appendSchedule({
        label: '',
        startPeriod: 1,
        endPeriod: 3,
      })
    }

    // 期間数を変更する処理
    const handleMaxPeriodChange = (newMaxPeriod: number) => {
      if (newMaxPeriod < 1 || newMaxPeriod > 24) return

      setValue('maxPeriod', newMaxPeriod)

      // 期間を短くした場合、はみ出たスケジュールを自動的に期間内に収める
      scheduleFields.forEach((field, index) => {
        const currentItem = watchedScheduleItems?.[index] || field
        if (currentItem.endPeriod > newMaxPeriod) {
          updateSchedule(index, {
            ...currentItem,
            endPeriod: newMaxPeriod,
            startPeriod: Math.min(currentItem.startPeriod, newMaxPeriod),
          })
        }
      })
    }

    // ドラッグ開始時の処理
    // type: 'start'=左端リサイズ, 'end'=右端リサイズ, 'move'=バー全体移動
    const handleMouseDown = (
      index: number,
      type: 'start' | 'end' | 'move',
      e: React.MouseEvent
    ) => {
      e.preventDefault()
      const currentItem = watchedScheduleItems?.[index] || scheduleFields[index]
      setDragging({
        index,
        type,
        initialX: e.clientX,
        initialPeriod:
          type === 'start'
            ? currentItem.startPeriod
            : type === 'end'
              ? currentItem.endPeriod
              : currentItem.startPeriod,
        initialStartPeriod: currentItem.startPeriod,
        initialEndPeriod: currentItem.endPeriod,
      })
    }

    // ドラッグ中のマウス移動処理
    // マウスの移動距離を期間数に変換し、スケジュールバーの位置を更新
    const handleMouseMove = (e: MouseEvent) => {
      if (!dragging || !scheduleContainerRef.current) return

      // マウスの移動距離を期間数に変換
      const containerRect = scheduleContainerRef.current.getBoundingClientRect()
      const cellWidth = containerRect.width / currentMaxPeriod
      const deltaX = e.clientX - dragging.initialX
      const deltaPeriods = Math.round(deltaX / cellWidth)

      const currentItem =
        watchedScheduleItems?.[dragging.index] || scheduleFields[dragging.index]

      if (dragging.type === 'move') {
        // バー全体を移動（長さを保持したまま左右にスライド）
        const periodSpan =
          (dragging.initialEndPeriod ?? 0) - (dragging.initialStartPeriod ?? 0)
        const newStartPeriod = Math.max(
          1,
          Math.min(
            currentMaxPeriod - periodSpan,
            dragging.initialPeriod + deltaPeriods
          )
        )
        const newEndPeriod = newStartPeriod + periodSpan

        updateSchedule(dragging.index, {
          ...currentItem,
          startPeriod: newStartPeriod,
          endPeriod: newEndPeriod,
        })
      } else {
        // 端をリサイズ（開始または終了期間のみ変更）
        const newPeriod = Math.max(
          1,
          Math.min(currentMaxPeriod, dragging.initialPeriod + deltaPeriods)
        )

        if (dragging.type === 'start') {
          // 左端リサイズ：終了期間を超えないように制限
          if (newPeriod <= currentItem.endPeriod) {
            updateSchedule(dragging.index, {
              ...currentItem,
              startPeriod: newPeriod,
            })
          }
        } else {
          // 右端リサイズ：開始期間を下回らないように制限
          if (newPeriod >= currentItem.startPeriod) {
            updateSchedule(dragging.index, {
              ...currentItem,
              endPeriod: newPeriod,
            })
          }
        }
      }
    }

    // ドラッグ終了処理：ドラッグ状態をクリア
    const handleMouseUp = () => {
      setDragging(null)
    }

    // ドラッグ中のみマウスイベントをdocumentに登録（これによりマウスがテーブル外に出てもドラッグを継続できる）
    useEffect(() => {
      if (dragging) {
        document.addEventListener('mousemove', handleMouseMove)
        document.addEventListener('mouseup', handleMouseUp)
        return () => {
          document.removeEventListener('mousemove', handleMouseMove)
          document.removeEventListener('mouseup', handleMouseUp)
        }
      }
    }, [dragging, currentMaxPeriod])

    if (!slideExecutionPlan) {
      return (
        <div className="flex items-center justify-center h-64">
          <p>実行計画&必要なリソースが見つかりません</p>
        </div>
      )
    }

    // スケジュール表を描画する関数
    // 編集モード時：ドラッグ可能な緑バー、削除ボタン、入力フィールドを表示
    // 閲覧モード時：静的な表示のみ
    const renderScheduleGrid = (isEditMode = false) => {
      return (
        <div ref={scheduleContainerRef} className="mt-6">
          <table className="w-full border border-main-darkgray table-fixed">
            <thead>
              <tr className="bg-main-gray">
                {isEditMode && (
                  <th className="border-b border-r border-main-darkgray w-16 p-2"></th>
                )}
                <th className="border-b border-r border-main-darkgray p-3 text-left text-sm font-bold w-40 lg:w-64">
                  項目
                </th>
                {Array.from({ length: currentMaxPeriod }, (_, i) => (
                  <th
                    key={i}
                    className="border-b border-r border-main-darkgray p-2 text-center text-base font-bold"
                    style={{ width: `${100 / currentMaxPeriod}%` }}
                  >
                    {i + 1}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {scheduleFields.map((field, index) => (
                <tr key={field.id}>
                  {isEditMode && (
                    <td
                      className={`border-r border-main-darkgray p-2 text-center ${index === scheduleFields.length - 1 ? 'border-b' : ''}`}
                    >
                      {/* 削除ボタンは項目が2つ以上ある場合のみ活性化 */}
                      <FlatButton
                        onClick={() => removeSchedule(index)}
                        className="!p-1 !text-xs !bg-main-red w-full"
                        type="button"
                        disabled={scheduleFields.length <= 1}
                      >
                        削除
                      </FlatButton>
                    </td>
                  )}
                  <td
                    className={`border-r border-main-darkgray p-2 ${index === scheduleFields.length - 1 ? 'border-b' : ''}`}
                  >
                    {isEditMode ? (
                      <TextInput
                        register={register(`scheduleItems.${index}.label`)}
                        placeholder="項目名"
                        className="text-sm"
                      />
                    ) : (
                      <p className="text-sm font-medium">{field.label}</p>
                    )}
                  </td>
                  {/* 期間数分のセルを生成（1〜currentMaxPeriod） */}
                  {Array.from(
                    { length: currentMaxPeriod },
                    (_, periodIndex) => {
                      // 編集中は監視中の値、閲覧時は固定値を使用
                      const scheduleItem = isEditMode
                        ? watchedScheduleItems?.[index] || field
                        : field
                      // このセルに緑バーを表示するかチェック（期間内かどうか）
                      const isInSchedule =
                        periodIndex + 1 >= scheduleItem.startPeriod &&
                        periodIndex + 1 <= scheduleItem.endPeriod
                      // 緑バーの左端・右端かを判定（角丸スタイル用）
                      const isStartPeriod =
                        periodIndex + 1 === scheduleItem.startPeriod
                      const isEndPeriod =
                        periodIndex + 1 === scheduleItem.endPeriod

                      return (
                        <td
                          key={periodIndex}
                          className={`border-r border-main-darkgray h-12 relative ${index === scheduleFields.length - 1 ? 'border-b' : ''}`}
                        >
                          {isInSchedule && (
                            <div
                              className={`absolute top-1/2 -translate-y-1/2 h-8 bg-main-green ${
                                isStartPeriod && isEndPeriod
                                  ? 'rounded mx-1'
                                  : isStartPeriod
                                    ? 'rounded-l ml-1 -mr-px'
                                    : isEndPeriod
                                      ? 'rounded-r -ml-px mr-1'
                                      : '-ml-px -mr-px'
                              } ${isEditMode ? 'cursor-move' : ''}`}
                              style={{
                                left: isStartPeriod ? '4px' : '0',
                                right: isEndPeriod ? '4px' : '0',
                              }}
                              onMouseDown={
                                isEditMode
                                  ? (e) => handleMouseDown(index, 'move', e)
                                  : undefined
                              }
                            >
                              {isEditMode && isStartPeriod && (
                                <div
                                  className="absolute left-0 top-0 w-3 h-full bg-main-darkgreen cursor-ew-resize rounded-l"
                                  onMouseDown={(e) => {
                                    e.stopPropagation()
                                    handleMouseDown(index, 'start', e)
                                  }}
                                />
                              )}
                              {isEditMode && isEndPeriod && (
                                <div
                                  className="absolute right-0 top-0 w-3 h-full bg-main-darkgreen cursor-ew-resize rounded-r"
                                  onMouseDown={(e) => {
                                    e.stopPropagation()
                                    handleMouseDown(index, 'end', e)
                                  }}
                                />
                              )}
                            </div>
                          )}
                        </td>
                      )
                    }
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )
    }

    return (
      <div ref={ref}>
        <SlideContainer>
          <SlideHeader
            title="4. 実行計画 & 必要なリソース"
            isEditing={isEditing}
            isUpdating={isUpdating}
            isDirty={isDirty}
            onUpdate={handleSubmit(onFormSubmit)}
          />

          <div className="slide-content px-8 py-6">
            <div className="mb-8">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold text-lg">ロードマップ</h3>
                {isEditing && (
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <span className="text-sm">期間</span>
                      <div className="relative">
                        <input
                          type="number"
                          min="1"
                          max="24"
                          value={currentMaxPeriod}
                          readOnly
                          className="w-16 px-2 py-1 border border-main-gray rounded text-center bg-white"
                          {...register('maxPeriod', { valueAsNumber: true })}
                        />
                        <div className="absolute right-1 top-1/2 -translate-y-1/2 flex flex-col">
                          <button
                            type="button"
                            className="w-4 h-3 flex items-center justify-center text-xs text-main-darkgray hover:bg-main-gray"
                            onClick={() => {
                              const newValue = Math.min(
                                24,
                                currentMaxPeriod + 1
                              )
                              handleMaxPeriodChange(newValue)
                            }}
                          >
                            ▲
                          </button>
                          <button
                            type="button"
                            className="w-4 h-3 flex items-center justify-center text-xs text-main-darkgray hover:bg-main-gray"
                            onClick={() => {
                              const newValue = Math.max(1, currentMaxPeriod - 1)
                              handleMaxPeriodChange(newValue)
                            }}
                          >
                            ▼
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {isEditing ? renderScheduleGrid(true) : renderScheduleGrid(false)}

              {isEditing && (
                <div className="mt-4">
                  <FlatButton onClick={handleAddSchedule} type="button">
                    項目を追加
                  </FlatButton>
                </div>
              )}
            </div>
            {isEditing ? (
              <TextareaInput register={register('resource')} rows={4} />
            ) : (
              <p className="text-base whitespace-pre-wrap">
                {slideExecutionPlan.resource}
              </p>
            )}

            {isEditing && (
              <div className="mt-8">
                <AiRegenerateSection
                  register={registerAi}
                  handleSubmit={handleSubmitAi}
                  aiInstruction={aiInstruction}
                  isUpdating={isUpdating}
                  onSubmit={onAiRegenerate}
                />
              </div>
            )}
            <div className="slide-footer mt-20">
              <p className="text-xs text-main-darkgray text-center mb-2">
                この企画書は「SDGs 共創
                Lodge」の共創アイディア生成AIで生成しました。
              </p>
              <img
                src={logoGray}
                alt="SDGs共創Lodge Logo"
                className="absolute bottom-2 right-4 w-20 h-auto"
              />
            </div>
          </div>
        </SlideContainer>
      </div>
    )
  }
)

ExecutionPlanSlide.displayName = 'ExecutionPlanSlide'
